import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { ApiAuth } from 'app/domain/api';
import { AgentDTO, CompanyDTO, CustomerDTO } from 'app/domain/api.model';
import { UserDTO } from 'app/domain/auth.model';
import { AgentService } from 'app/services/agent.service';
import { AppUserService } from 'app/services/appuser.service';
import { CompanyService } from 'app/services/company.service';
import { CustomerService } from 'app/services/customer.service';
import { SessionService } from 'app/services/session.service';
import { SupplierService } from 'app/services/supplier.service';
import { catchError, forkJoin, map, Observable, of, Subject, switchMap, takeUntil, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _agentService = inject(AgentService);
    private _customerService = inject(CustomerService);
    private _supplierService = inject(SupplierService);
    private _companyService = inject(CompanyService);
    private _appUserService = inject(AppUserService);
    private _sessionService = inject(SessionService);

    protected _unsubscribeAll: Subject<any> = new Subject<any>();

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }
    

    set refreshToken(token: string)
    {
        let expiredDate = new Date();
        expiredDate.setDate( expiredDate.getDate() + 30); 
        localStorage.setItem('refreshToken', token); 
    }

    get refreshToken(): string
    {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

      /**
     * Forgot password
     *
     * @param email
     */
      forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(ApiAuth.Login, credentials).pipe(
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.token;
                this.refreshToken = response.refreshToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                if(response.user)
                {
                    this._sessionService.start();
                    // this.initData();
                    return this.initData().pipe(
                        map(() => response) 
                    );
                }

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient
            .post(ApiAuth.RefreshLogin, {
                accessToken: this.accessToken,
                refreshToken: this.refreshToken
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.token) 
                    {
                        this.accessToken = response.token;

                        // Set the authenticated flag to true
                        this._authenticated = true;

                        if ( response.refreshToken )
                        {
                            this.refreshToken = response.refreshToken;
                        }
                    
                        // Store the user on the user service
                        this._userService.user = response.user;
                        
                        if(response.user)
                        {
                            this._sessionService.start();
                            // this.initData();
                            return this.initData().pipe(
                                map(() => true) 
                            );
                        }

                        // Return true
                        return of(true);
                    }
                    else
                    {
                        this._authenticated = false;
                        return of(false);
                    } 
                })
            );
    }

    

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('settings');
        localStorage.removeItem('user');

        this._authenticated = false;

        this._userService.user = null;

        this._sessionService.stop();

        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    
//   public initData(): void{

//     this._appUserService.getAll()
//     .pipe(takeUntil(this._unsubscribeAll))
//     .subscribe((data: UserDTO[]) => {
//     });

//     this._agentService.getLogged()
//     .pipe(takeUntil(this._unsubscribeAll))
//     .subscribe((data: AgentDTO) => {
//     });
    
//     this._companyService.getAll()
//       .pipe(takeUntil(this._unsubscribeAll))
//       .subscribe((data: CompanyDTO[]) => { });

//     this._customerService.getAll()
//       .pipe(takeUntil(this._unsubscribeAll))
//       .subscribe((data: CustomerDTO[]) => { });
//   }

  public initData(): Observable<any> {
    return forkJoin([
        this._appUserService.getAll(),
        this._agentService.getLogged(),
        this._companyService.getAll(),
        this._customerService.getAll(),
        this._supplierService.getAll()
    ]);
  }
}
